import { createActions, createReducer } from "reduxsauce";
import { cloneDeep, set, forEach, isEmpty } from "@utils/lodash";
import { updateStateGeneric } from "@redux-core/utils";

export { default as thunks } from "./thunks";
export { default as utils } from "./utils";

/* ------------- Initial State ------------- */
export const INITIAL_STATE = {
	locationApprovals: {
		list: {
			filter: {
				type: 30,
				displayCount: 25,
				pageNumber: 1,
				pagingToken: null,
				next: false,
				search: "",
			},
			count: 0,
			itemList: [],
			selection: [],
			counts: {
				All: "",
				Pending: "",
				Approved: "",
				Denied: "",
			},
		},
		view: {},
		modals: {
			locationDenialUnassign: {
				show: false,
				settings: {
					multiple: false,
					selectedLocations: [],
					locationId: "",
					locationName: "",
				},
				officerAction: "",
			},
		},
	},
	jobApprovals: {
		list: {
			filter: {
				type: 30,
				displayCount: 25,
				pageNumber: 1,
				pagingToken: null,
				next: false,
			},
			count: 0,
			itemList: [],
			selection: [],
			counts: {
				Pending: 0,
				Approved: 0,
				Denied: 0,
			},
		},
	},
	taxExemptions: {
		list: {
			filter: {
				status: "pending",
				displayCount: 25,
				pageNumber: 1,
			},
			count: 0,
			itemList: [],
			counts: {
				All: 0,
				Pending: 0,
				Approved: 0,
				Denied: 0,
			},
		},
	},
	shifts: {
		list: {
			data: [],
			officers: {},
			totalCount: 0,
			expandedRowIds: [],
			filters: {
				displayCount: 25,
				pageNumber: 1,
				officerName: null,
				officerRank: null,
				officerStatus: null,
			},
		},
		form: {
			inputData: {
				Name: {
					value: "",
					validation: null,
					rules: "required",
				},
				SupervisorId: {
					value: "",
					validation: null,
					rules: "required",
				},
				Officers: {
					value: [],
				},
			},
			title: "Add Shift Form",
			searchText: "",
			searchOfficersList: [],
			buttonStatus: "",
			id: null,
			_defaultSupervisorName: null,
		},
	},
	loaders: {
		locationApprovals: false,
		locationApprovalsCounts: false,
		jobApprovals: false,
		taxExemptions: false,
		taxExemptionsCounts: false,
		jobApprovalsCounts: false,
		shifts: false,
		searchOfficersList: false,
		shift: false,
	},
	modals: {
		shiftForm: false,
	},
};

/* ------------- Types and Action Creators ------------- */
export const { Types, Creators } = createActions(
	{
		setModalOpen: ["page", "modal", "value"],
		setModalSettings: ["page", "modal", "settings"],
		updateLoaders: ["property", "value"],
		changePage: ["pageNumber"],
		changeDisplayCount: ["count"],
		receiveData: ["collection"],
		receiveList: ["page", "data"],
		updateCounts: ["page", "counts"],
		updateFilters: ["page", "filtersCollection"],
		updateSelection: ["page", "selection"],
		clearSelection: ["page"],
		clearList: ["page"],
		receiveShifts: ["data", "totalCount"],
		toggleExpandedShiftRows: ["data"],
		receiveOfficers: ["id", "data", "loading"],
		updateShiftsFilters: ["data"],
		resetShifts: null,
		resetShiftForm: null,
		updateShiftForm: ["property", "value"],
		toggleModal: ["name", "value"],
	},
	{ prefix: "@admin/" },
);

/* ------------- Reducers ------------- */

export const reducer = createReducer(INITIAL_STATE, {
	[Types.RECEIVE_LIST]: (state, { page, data }) => {
		const newState = cloneDeep(state);
		const { Count, DisplayCount, HasNext, ItemList, PagingToken, Page } = data;

		set(newState, [page, "list", "count"], Count);
		set(newState, [page, "list", "filter", "pageNumber"], Page);
		set(newState, [page, "list", "filter", "displayCount"], DisplayCount);
		set(newState, [page, "list", "filter", "next"], HasNext);
		set(newState, [page, "list", "itemList"], ItemList);
		set(newState, [page, "list", "filter", "pagingToken"], PagingToken);

		return newState;
	},

	[Types.RECEIVE_SHIFTS]: (state, { data, totalCount }) => {
		const newState = cloneDeep(state);

		set(newState, "shifts.list.data", data);
		set(newState, "shifts.list.totalCount", totalCount);
		set(newState, "shifts.list.officers", {});
		set(newState, "shifts.list.expandedRowIds", []);

		return newState;
	},

	[Types.TOGGLE_EXPANDED_SHIFT_ROWS]: (state, { data }) => {
		const newState = cloneDeep(state);

		set(newState, "shifts.list.expandedRowIds", data);

		return newState;
	},

	[Types.RECEIVE_OFFICERS]: (state, { id, data, loading }) => {
		const newState = cloneDeep(state);

		set(newState, `shifts.list.officers.shift_${id}.data`, data);
		set(newState, `shifts.list.officers.shift_${id}.loading`, loading);

		return newState;
	},

	[Types.UPDATE_SHIFTS_FILTERS]: (state, { data }) => {
		const newState = cloneDeep(state);

		set(newState, "shifts.list.filters", data);

		return newState;
	},

	[Types.RESET_SHIFTS]: state => ({
		...state,
		shifts: INITIAL_STATE.shifts,
	}),

	[Types.RESET_SHIFT_FORM]: state => ({
		...state,
		shifts: {
			...state.shifts,
			form: INITIAL_STATE.shifts.form,
		},
	}),

	[Types.UPDATE_SHIFT_FORM]: (state, { property, value }) => {
		const newState = cloneDeep(state);

		set(newState, `shifts.form.${property}`, value);

		return newState;
	},

	[Types.TOGGLE_MODAL]: (state, { name, value }) => {
		const newState = cloneDeep(state);

		set(newState, `modals.${name}`, value);

		return newState;
	},

	[Types.UPDATE_LOADERS]: (state, { property, value }) =>
		updateStateGeneric({ state, property: `loaders.${property}`, value }),

	[Types.CHANGE_PAGE]: (state, { page, pageNumber }) => {
		const newState = cloneDeep(state);
		set(newState, [page, "list", "pageNumber"], pageNumber);
		return newState;
	},

	[Types.CHANGE_DISPLAY_COUNT]: (state, { page, count }) => {
		const newState = cloneDeep(state);
		set(newState, [page, "list", "displayCount"], count);
		return newState;
	},

	[Types.UPDATE_COUNTS]: (state, { page, counts }) => {
		const newState = cloneDeep(state);
		set(newState, [page, "list", "counts"], counts);
		return newState;
	},

	[Types.RECEIVE_DATA]: (state, { collection }) => {
		if (!isEmpty(collection)) {
			const newState = cloneDeep(state);

			forEach(collection, ({ property, value }) => {
				set(newState, property, value);
			});

			return newState;
		}
		return state;
	},

	[Types.SET_MODAL_OPEN]: (state, { page, modal, value }) => {
		const newState = cloneDeep(state);

		set(newState, [page, "modals", modal, "show"], value);

		return newState;
	},

	[Types.SET_MODAL_SETTINGS]: (state, { page, modal, settings }) => {
		const newState = cloneDeep(state);

		set(newState, [page, "modals", modal, "settings"], settings);

		return newState;
	},

	[Types.UPDATE_FILTERS]: (state, { page, filtersCollection }) => {
		const newState = cloneDeep(state);

		forEach(filtersCollection, ({ property, value }) => {
			set(newState, [page, "list", "filter", property], value);
		});

		return newState;
	},

	[Types.UPDATE_SELECTION]: (state, { page, selection }) => {
		const newState = cloneDeep(state);
		set(newState, [page, "list", "selection"], selection);
		return newState;
	},

	[Types.CLEAR_SELECTION]: (state, { page }) => {
		const newState = cloneDeep(state);
		set(newState, [page, "list", "selection"], []);

		return newState;
	},

	[Types.CLEAR_LIST]: (state, { page }) => {
		const newState = cloneDeep(state);
		set(newState, [page, "list", "itemList"], []);
		return newState;
	},
});

export default Creators;
